// ═══════════════════════════════════════════════════════════════════════════════
// PAGE TARIFS ABRACACOINS - Formules d'abonnement et packs de recharge
// Accessible depuis les Cockpits Pro et Particulier
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Check, X, Crown, Zap, Sparkles, ArrowRight, ArrowLeft,
  MessageCircle, FileText, Image, Video, Mic, Bot,
  Users, Building2, Star, Coins, ShoppingCart, ChevronRight,
  Loader2, AlertCircle, Gift
} from 'lucide-react';
import FounderCountdown from '../../components/abracacoins/FounderCountdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// Données statiques (backup si API indisponible)
// ═══════════════════════════════════════════════════════════════════════════════

const DEFAULT_PLANS = {
  gratuit: {
    name: 'Gratuit',
    price_monthly: 0,
    monthly_coins: 100,
    founder_coins: 100,
    features: [
      'Chat IA basique',
      'Recherche artisans gratuite',
      'Agenda simple',
      '100 AbracaCoins/mois',
    ],
    not_included: [
      'Devis & Factures IA',
      'Transcription audio',
      'Génération images',
    ],
    cta: 'Commencer gratuitement',
    color: 'gray',
  },
  pro: {
    name: 'Pro',
    price_monthly: 49,
    monthly_coins: 5000,
    founder_coins: 10000,
    popular: true,
    features: [
      'Tout le Gratuit +',
      'CRM complet',
      'Devis & Factures IA',
      'Pipeline commercial',
      'Transcription audio',
      'Génération images',
      '5 000 AbracaCoins/mois',
      'Support prioritaire',
    ],
    not_included: [
      'Multi-utilisateurs',
      'API accès',
    ],
    cta: 'Choisir Pro',
    color: 'orange',
  },
  business: {
    name: 'Business',
    price_monthly: 99,
    monthly_coins: 15000,
    founder_coins: 30000,
    features: [
      'Tout le Pro +',
      'Multi-utilisateurs (5)',
      'Transcription illimitée',
      'Analyses comptables',
      'Site web inclus',
      'API accès',
      '15 000 AbracaCoins/mois',
      'Support dédié',
    ],
    not_included: [],
    cta: 'Choisir Business',
    color: 'emerald',
  },
};

// Mapping des features techniques vers labels lisibles
const FEATURE_LABELS = {
  profil_certifie: 'Profil certifié',
  geolocalisation: 'Géolocalisation',
  crm: 'CRM complet',
  devis_ia: 'Devis IA',
  factures_ia: 'Factures IA',
  transcription: 'Transcription audio',
  badge_pro: 'Badge Pro',
  reseaux_sociaux: 'Réseaux sociaux pilotés',
  google_my_business: 'Google My Business',
  comptabilite_ia: 'Comptabilité IA',
  analytics: 'Analyses avancées',
  site_web: 'Site web inclus',
  support_prioritaire: 'Support prioritaire',
};

const DEFAULT_PACKS = {
  small: { name: 'Petit', coins: 1000, price: 4.99, popular: false },
  medium: { name: 'Moyen', coins: 5000, price: 24.99, popular: true },
  large: { name: 'Gros', coins: 20000, price: 79.99, popular: false },
};

const ACTIONS_EXAMPLES = [
  { action: 'Chat simple', cost: 1, icon: MessageCircle },
  { action: 'Email professionnel', cost: 3, icon: FileText },
  { action: 'Génération image', cost: 5, icon: Image },
  { action: 'Devis IA simple', cost: 25, icon: FileText },
  { action: 'Devis IA complexe', cost: 40, icon: FileText },
  { action: 'Transcription (1 min)', cost: 1, icon: Mic },
  { action: 'Analyse document', cost: 50, icon: FileText },
  { action: 'Landing page', cost: 150, icon: Bot },
];

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function TarifsAbracaCoinsPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [packs, setPacks] = useState(DEFAULT_PACKS);
  const [founderOffer, setFounderOffer] = useState({
    enabled: true,
    remaining_spots: 847,
    total_spots: 1000,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plans'); // 'plans' | 'recharge'
  const [isFounder, setIsFounder] = useState(false);
  
  // Récupérer les plans depuis l'API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(`${API_URL}/api/abracacoins/plans`);
        if (response.ok) {
          const data = await response.json();
          if (data.plans) setPlans(data.plans);
          if (data.recharge_packs) setPacks(data.recharge_packs);
          if (data.founder_offer) setFounderOffer(data.founder_offer);
        }
      } catch (err) {
        console.error('Erreur récupération plans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);
  
  // Récupérer le statut fondateur
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = localStorage.getItem('ecosystem_token') || 
                     localStorage.getItem('client_token');
        if (!token) return;
        
        const response = await fetch(`${API_URL}/api/abracacoins/subscription`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setIsFounder(data.is_founder || false);
        }
      } catch (err) {
        console.error('Erreur récupération abonnement:', err);
      }
    };
    fetchSubscription();
  }, []);
  
  const handleSelectPlan = (planId) => {
    // TODO: Intégrer avec Stripe Checkout
    console.log('Plan sélectionné:', planId);
    // navigate(`/checkout?plan=${planId}`);
  };
  
  const handleRecharge = (packId) => {
    // TODO: Intégrer avec Stripe Checkout
    console.log('Pack recharge:', packId);
    // navigate(`/checkout?pack=${packId}`);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="tarifs-abracacoins-page">
      {/* Header avec bouton retour */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
              <span className="text-sm font-medium">Retour</span>
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-2xl">🪙</span>
              <span className="font-bold text-slate-900">AbracaCoins</span>
            </div>
            
            <div className="w-20" /> {/* Spacer */}
          </div>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">
              Ta monnaie magique
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">
                pour tout créer
              </span>
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
              Les AbracaCoins alimentent toutes les fonctionnalités IA d'Abracadabati :
              chat, devis, factures, transcription, images...
            </p>
          </motion.div>
          
          {/* Tabs */}
          <div className="inline-flex items-center p-1 bg-slate-200 rounded-full mb-8">
            <button
              onClick={() => setActiveTab('plans')}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${
                activeTab === 'plans'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="tab-plans"
            >
              Formules
            </button>
            <button
              onClick={() => setActiveTab('recharge')}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${
                activeTab === 'recharge'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="tab-recharge"
            >
              Recharger
            </button>
          </div>
        </div>
      </section>
      
      {/* Offre Fondateur Banner avec compteur animé */}
      {founderOffer?.enabled && !isFounder && (
        <section className="px-4 mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white relative overflow-hidden">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
              </div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Crown size={28} className="text-yellow-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Offre Membre Fondateur</h3>
                    <p className="text-white/80 text-sm">
                      Double tes AbracaCoins chaque mois — <strong>à vie</strong>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  {/* Compteur animé */}
                  <FounderCountdown 
                    variant="banner" 
                    initialCount={founderOffer.remaining_spots || 847}
                    totalSpots={founderOffer.total_spots || 1000}
                  />
                  
                  <Link
                    to="/pro/fondateur"
                    className="flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-600 rounded-xl font-semibold hover:bg-white/90 transition-colors whitespace-nowrap"
                    data-testid="founder-offer-cta"
                  >
                    En profiter
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      )}
      
      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'plans' ? (
          <PlansSection
            key="plans"
            plans={plans}
            founderOffer={founderOffer}
            isFounder={isFounder}
            onSelectPlan={handleSelectPlan}
            loading={loading}
          />
        ) : (
          <RechargeSection
            key="recharge"
            packs={packs}
            onRecharge={handleRecharge}
            loading={loading}
          />
        )}
      </AnimatePresence>
      
      {/* Actions & Coûts */}
      <section className="py-12 px-4 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Combien coûtent les actions ?
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ACTIONS_EXAMPLES.map((item, i) => (
              <motion.div
                key={item.action}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-slate-50 rounded-xl p-4 text-center"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 bg-slate-200 rounded-lg mb-2">
                  <item.icon size={20} className="text-slate-600" />
                </div>
                <p className="text-sm font-medium text-slate-700 mb-1">{item.action}</p>
                <p className="text-lg font-bold text-amber-600">
                  {item.cost} 🪙
                </p>
              </motion.div>
            ))}
          </div>
          
          <p className="text-center text-sm text-slate-500 mt-6">
            Les actions gratuites (recherche artisans, navigation) ne consomment pas d'AbracaCoins.
          </p>
        </div>
      </section>
      
      {/* FAQ */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Questions fréquentes
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: "Qu'est-ce que les AbracaCoins ?",
                a: "Les AbracaCoins sont ta monnaie virtuelle pour utiliser les fonctionnalités IA : chat intelligent, création de devis, génération d'images, transcription audio, etc. Chaque action a un coût en AbracaCoins.",
              },
              {
                q: "Mes AbracaCoins non utilisés se cumulent-ils ?",
                a: "Ton quota mensuel se renouvelle chaque mois. Les AbracaCoins non utilisés du quota mensuel ne se cumulent pas. En revanche, les AbracaCoins achetés via les packs de recharge vont dans ta réserve et n'expirent jamais.",
              },
              {
                q: "C'est quoi l'offre Membre Fondateur ?",
                a: "Les 1 000 premiers membres Pro ou Business bénéficient du double d'AbracaCoins chaque mois, à vie. Par exemple, un Pro Fondateur reçoit 10 000 AbracaCoins/mois au lieu de 5 000.",
              },
              {
                q: "Je peux changer de formule ?",
                a: "Oui ! Tu peux upgrader ou downgrader à tout moment. Le changement prend effet immédiatement et ta facturation est ajustée au prorata.",
              },
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">{faq.q}</h4>
                <p className="text-slate-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION FORMULES
// ═══════════════════════════════════════════════════════════════════════════════

function PlansSection({ plans, founderOffer, isFounder, onSelectPlan, loading }) {
  const planOrder = ['gratuit', 'pro', 'business'];
  
  // Toujours utiliser DEFAULT_PLANS pour les features lisibles
  // L'API ne retourne que les coins, pas les features formatées
  
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="px-4 pb-12"
    >
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          {planOrder.map((planId, i) => {
            // Toujours utiliser DEFAULT_PLANS pour les features lisibles
            const plan = DEFAULT_PLANS[planId];
            // Merge avec les données API si disponibles (pour les coins à jour)
            const apiPlan = plans[planId];
            const mergedPlan = {
              ...plan,
              monthly_coins: apiPlan?.monthly_coins || plan.monthly_coins,
              founder_coins: apiPlan?.monthly_coins_founder || plan.founder_coins,
            };
            const isPopular = mergedPlan.popular;
            
            return (
              <motion.div
                key={planId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`relative bg-white rounded-2xl overflow-hidden ${
                  isPopular
                    ? 'ring-2 ring-orange-500 shadow-xl shadow-orange-500/10'
                    : 'border border-slate-200'
                }`}
                data-testid={`plan-card-${planId}`}
              >
                {/* Badge populaire */}
                {isPopular && (
                  <div className="absolute top-0 right-0 px-4 py-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold">
                    POPULAIRE
                  </div>
                )}
                
                <div className="p-6">
                  {/* Nom */}
                  <h3 className="text-xl font-black text-slate-900 mb-1">
                    {mergedPlan.name}
                  </h3>
                  
                  {/* Prix */}
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-4xl font-black text-slate-900">
                      {mergedPlan.price_monthly}€
                    </span>
                    <span className="text-slate-500">/mois</span>
                  </div>
                  
                  {/* AbracaCoins */}
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl mb-4">
                    <span className="text-xl">🪙</span>
                    <div>
                      <p className="font-bold text-amber-700">
                        {(mergedPlan.monthly_coins || 0).toLocaleString()} AbracaCoins/mois
                      </p>
                      {founderOffer?.enabled && mergedPlan.founder_coins > mergedPlan.monthly_coins && (
                        <p className="text-xs text-emerald-600 font-medium">
                          Fondateur : {(mergedPlan.founder_coins || 0).toLocaleString()} 🪙
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {(mergedPlan.features || []).map((feature, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm">
                        <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                    {(mergedPlan.not_included || []).map((feature, j) => (
                      <li key={`not-${j}`} className="flex items-start gap-2 text-sm">
                        <X size={16} className="text-slate-300 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-400">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA */}
                  <button
                    onClick={() => onSelectPlan(planId)}
                    className={`w-full py-3 rounded-xl font-semibold transition-all ${
                      isPopular
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-lg shadow-orange-500/25'
                        : mergedPlan.color === 'emerald'
                          ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                          : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                    }`}
                    data-testid={`select-plan-${planId}`}
                  >
                    {mergedPlan.cta || 'Choisir'}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION RECHARGE
// ═══════════════════════════════════════════════════════════════════════════════

function RechargeSection({ packs, onRecharge, loading }) {
  const packOrder = ['small', 'medium', 'large'];
  
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="px-4 pb-12"
    >
      <div className="max-w-4xl mx-auto">
        {/* Explication */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 flex items-start gap-3">
          <AlertCircle size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800">
              <strong>Besoin de plus d'AbracaCoins ?</strong> Les packs de recharge vont directement dans ta <strong>réserve</strong> et n'expirent jamais. Ils sont utilisés automatiquement quand ton quota mensuel est épuisé.
            </p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {packOrder.map((packId, i) => {
            const pack = packs[packId] || DEFAULT_PACKS[packId];
            const isPopular = pack.popular;
            const pricePerCoin = (pack.price / pack.coins * 1000).toFixed(2);
            
            return (
              <motion.div
                key={packId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`relative bg-white rounded-2xl p-6 ${
                  isPopular
                    ? 'ring-2 ring-blue-500 shadow-xl shadow-blue-500/10'
                    : 'border border-slate-200'
                }`}
                data-testid={`pack-card-${packId}`}
              >
                {/* Badge meilleur rapport */}
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                    MEILLEUR RAPPORT
                  </div>
                )}
                
                {/* Header */}
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-2xl mb-3">
                    <span className="text-3xl">🪙</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{pack.name}</h3>
                </div>
                
                {/* Coins */}
                <div className="text-center mb-4">
                  <p className="text-3xl font-black text-amber-600">
                    {(pack.coins || 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-500">AbracaCoins</p>
                </div>
                
                {/* Prix */}
                <div className="text-center mb-4">
                  <p className="text-2xl font-bold text-slate-900">{pack.price}€</p>
                  <p className="text-xs text-slate-500">
                    soit {pricePerCoin}€ / 1000 🪙
                  </p>
                </div>
                
                {/* CTA */}
                <button
                  onClick={() => onRecharge(packId)}
                  className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                    isPopular
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                  }`}
                  data-testid={`buy-pack-${packId}`}
                >
                  <ShoppingCart size={18} />
                  Acheter
                </button>
              </motion.div>
            );
          })}
        </div>
        
        {/* Note sécurité */}
        <p className="text-center text-xs text-slate-500 mt-6">
          Paiement sécurisé par Stripe. Tu peux payer par carte bancaire ou Apple Pay.
        </p>
      </div>
    </motion.section>
  );
}
